import React, {
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback
} from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";
import { Container, PopoverPanel } from "./styles";
import { AuthContext } from "../../context/AuthProvider";
import { theme } from "../../globalStyles";
import IconButtonV2 from "../IconButtonV2";
import { Button } from "../Buttons/Button";
import EditorStemsEmblem from "../EditorStemsEmblem";
import { EditorContext } from "../../editor/context/EditorContext/EditorProvider";
import gql from "graphql-tag";
import { useLazyQuery } from "react-apollo";

const GET_EDITOR_DEMOS = gql`
  {
    demoTracks {
      id
    }
  }
`;

/*
 * This component is a button to access the multitrack editor. It's on
 * the bottom play bar, action bars, /browse, etc. Internally it checks
 * for the current user's access permissions for the given track, lights
 * up the button if accessible or disables if inaccessible. Includes hover
 * tooltips and popovers.
 */
const EditorPopoverButton = ({
  track,
  size = "21px",
  yOffset = "75px",
  anchor = "bottom",
  accessibleColor = theme.highlight,
  inaccessibleColor = theme.textDark,
  buttonStyle = {}
}) => {
  const auth = useContext(AuthContext);
  const editor = useContext(EditorContext);

  const [isFullMessage, setIsFullMessage] = useState(false);
  const [hasSeenFullMessage, setHasSeenFullMessage] = useState(
    localStorage.getItem("user-seen-full-editor-popover") || false
  );
  const [
    canAccessCurrentTrackEditor,
    setCanAccessCurrentTrackEditor
  ] = useState(false);
  const timerId = useRef(null);
  const containerRef = useRef(null);
  const popoverRef = useRef(null);

  // get a list of demo tracks we could load up
  const [getDemoTrackIds, resDemoTracks] = useLazyQuery(GET_EDITOR_DEMOS, {
    onCompleted: () => {
      spawnEditorDemo();
    }
  });

  const { push } = useHistory();

  // simple tooltip, instant hover feedback (via IconButtonV2)
  const tooltipText = canAccessCurrentTrackEditor
    ? "Open in Multitrack Editor"
    : track.editorOpenable
    ? "Subscribe for access to the multitrack editor"
    : "Track is not available in the multitrack editor";

  // Track mouse presence in both the button and the popover we know when to dismiss
  const hoverIntent = useRef({ button: false, popover: false });
  const dismissTimerId = useRef(null);

  const scheduleDismiss = () => {
    clearTimeout(dismissTimerId.current);
    dismissTimerId.current = setTimeout(() => {
      if (!hoverIntent.current.button && !hoverIntent.current.popover) {
        setIsFullMessage(false);
      }
    }, 100);
  };

  const handleMouseEnter = () => {
    hoverIntent.current.button = true;
    clearTimeout(dismissTimerId.current);
    if (isFullMessage) return;

    const showFullMessage = () => {
      setIsFullMessage(true);
      setHasSeenFullMessage(true);
      localStorage.setItem("user-seen-full-editor-popover", true);
    };

    if (canAccessCurrentTrackEditor && !hasSeenFullMessage) {
      showFullMessage();
    } else if (track.editorOpenable) {
      timerId.current = setTimeout(showFullMessage, 1500);
    }
  };

  const handleMouseLeave = () => {
    hoverIntent.current.button = false;
    clearTimeout(timerId.current);
    scheduleDismiss();
  };

  const handlePopoverMouseEnter = () => {
    hoverIntent.current.popover = true;
    clearTimeout(dismissTimerId.current);
  };

  const handlePopoverMouseLeave = () => {
    hoverIntent.current.popover = false;
    scheduleDismiss();
  };

  // Click outside to dismiss the extended popover
  useEffect(() => {
    if (!isFullMessage) return;
    const handleClickOutside = e => {
      const inContainer = containerRef.current?.contains(e.target);
      const inPopover = popoverRef.current?.contains(e.target);
      if (!inContainer && !inPopover) {
        setIsFullMessage(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isFullMessage]);

  useEffect(() => {
    return () => {
      clearTimeout(timerId.current);
      clearTimeout(dismissTimerId.current);
    };
  }, []);

  // Close popover when editor drawer opens
  useEffect(() => {
    if (editor.drawerState.visible) {
      clearTimeout(timerId.current);
      setIsFullMessage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.drawerState.visible]);

  useEffect(() => {
    setCanAccessCurrentTrackEditor(auth.canAccessTrackEditor(track).granted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, track.id]);

  const spawnEditorDemo = useCallback(() => {
    if (!resDemoTracks.data) return;
    if (resDemoTracks.data.demoTracks.length > 0) {
      const demoTrackId =
        resDemoTracks.data.demoTracks[
          Math.floor(Math.random() * resDemoTracks.data.demoTracks.length)
        ].id;
      editor.requestTrackIdOpen(demoTrackId, true, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resDemoTracks.data]);

  // Position the popover via portal so it escapes any overflow:hidden ancestors
  const [popoverStyle, setPopoverStyle] = useState(null);

  const computePopoverStyle = useCallback(() => {
    if (!containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    const offset = parseInt(yOffset);
    return {
      position: "fixed",
      ...(anchor === "top"
        ? { top: rect.top + offset }
        : { bottom: window.innerHeight - rect.bottom + offset }),
      right: Math.max(window.innerWidth - rect.right, 16),
      zIndex: theme.zIndex.popover
    };
  }, [anchor, yOffset]);

  useLayoutEffect(() => {
    if (isFullMessage) {
      setPopoverStyle(computePopoverStyle());
    } else {
      setPopoverStyle(null);
    }
  }, [isFullMessage, computePopoverStyle]);

  // dismiss message on scroll
  useEffect(() => {
    if (!isFullMessage) return;
    const handleScroll = () => setIsFullMessage(false);
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [isFullMessage]);

  return (
    <Container
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <IconButtonV2
        type="open_editor"
        size={parseInt(size)}
        color={
          track && canAccessCurrentTrackEditor
            ? accessibleColor
            : inaccessibleColor
        }
        disabled={!canAccessCurrentTrackEditor}
        onClick={() => {
          if (track.editorOpenable && track.stem.length > 0) {
            editor.requestTrackOpen(track, true);
          }
        }}
        tooltip={!isFullMessage}
        tooltipText={tooltipText}
        label="Open in Editor"
        style={buttonStyle}
      />

      {isFullMessage &&
        popoverStyle &&
        createPortal(
          <PopoverPanel
            ref={popoverRef}
            style={popoverStyle}
            onMouseEnter={handlePopoverMouseEnter}
            onMouseLeave={handlePopoverMouseLeave}
          >
            <div id="editor-popover-container">
              <div id="editor-popover-title">
                {canAccessCurrentTrackEditor ? (
                  <div>
                    Multitrack Editor
                    <span className="editor-new-text">NEW</span>
                  </div>
                ) : (
                  "Subscribe for access to this track"
                )}
              </div>
              <div id="editor-popover-content" className="visible">
                {canAccessCurrentTrackEditor ? (
                  <div className="stems-emblem">
                    Make quick edits to the track from the stem files and
                    download your high-quality Custom Mix.
                    <EditorStemsEmblem />
                  </div>
                ) : (
                  <div>
                    The Multitrack Editor for this track is only available to
                    subscribers and license holders.
                    <div className="free-tier-ctas">
                      <Button
                        className="btn-editor-demo"
                        onClick={() => {
                          if (!resDemoTracks.called) getDemoTrackIds();
                          if (resDemoTracks.data) {
                            spawnEditorDemo();
                          }
                        }}
                      >
                        Try out editor
                      </Button>
                      <Button
                        className="btn-subscribe"
                        onClick={() => {
                          editor.close();
                          push("/subscription");
                        }}
                      >
                        Subscribe
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </PopoverPanel>,
          document.body
        )}
    </Container>
  );
};

export default EditorPopoverButton;
